import { buildMenuBreadcrumbs } from '@/layouts/breadcrumbUtils';
import { buildMenuTreeFromPermissions } from '@/layouts/buildMenuTreeFromPermissions';
import { buildSettingsSidebarFromMenuTree } from '@/layouts/buildSettingsSidebarFromMenuTree';
import { buildSidebarFromMenuTree } from '@/layouts/buildSidebarFromMenuTree';
import { filterSidebarByPermission } from '@/layouts/filterSidebarByPermission';
import { LayoutBreadcrumb } from '@/layouts/LayoutBreadcrumb';
import { MenuAccessContext } from '@/layouts/MenuAccessContext';
import {
  buildMenuKeyByUrlPath,
  resolveMenuAccessByPath,
} from '@/layouts/resolveMenuAccessByPath';
import { LoadingSpinner } from '@/shared/components/LoadingSpinner';
import { useIsMobileLayout } from '@/shared/hooks/useMediaQuery';
import { Button } from '@/shared/components/ui/button';
import {
  isPopupWindowRoute,
  isSettingsRoute,
  ROUTE_PATHS,
} from '@/shared/constants/routes';
import {
  ADMIN_ACCESS_ROLES,
  canOpenAccountManagement,
  hasRoleAccess,
  isMaster,
} from '@/shared/utils/authRoles';
import { useAuthStore } from '@/stores/authStore';
import { useUIStore } from '@/stores/uiStore';
import { Outlet, useLocation, useNavigate } from '@tanstack/react-router';
import { DoorOpen, Home, Menu } from 'lucide-react';
import React, { useEffect, useMemo } from 'react';
import { cn } from '@/shared/lib/utils';
import { toastApi } from '@/shared/utils/toast';

import SidebarMenu from './SidebarMenu';
import { adminSidebarMenuItems } from './sidebarMenuConfig';
import UserMenu from './UserMenu';

/** 마스터·관리 전용 설정 하위 경로 — 일반은 내 프로필로 되돌림 */
const SETTINGS_FULL_ACCESS_PATHS = [
  ROUTE_PATHS.SETTINGS_ACCOUNTS,
  ROUTE_PATHS.SETTINGS_AUDIT_LOG,
  ROUTE_PATHS.SETTINGS_PARTNERS,
] as const;

export const AdminLayout: React.FC = () => {
  const {
    sidebarCollapsed,
    setSidebarCollapsed,
    setMobileSidebarOpen,
    globalLoadingCount,
    globalLoadingTip,
  } = useUIStore();
  const { isAuthenticated, isInitialized, initialize, user, logout } =
    useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  // 마스터·관리·일반 로그인 허용 — 메뉴는 permissions로 필터
  const hasAccess = hasRoleAccess(user?.role, ADMIN_ACCESS_ROLES);
  const inSettingsMode = isSettingsRoute(location.pathname);
  const isMobileLayout = useIsMobileLayout();
  // 모바일 서랍에서는 메뉴 라벨을 항상 보여 준다
  const collapsed = sidebarCollapsed && !isMobileLayout;

  // 사이드바·URL 권한은 `/admin/auth/me` permissions만 사용 (GET /admin/menus 없음)
  const permissionMenus = useMemo(
    () => buildMenuTreeFromPermissions(user?.permissions),
    [user?.permissions],
  );

  const menuKeyByUrlPath = useMemo(
    () => buildMenuKeyByUrlPath(permissionMenus, user?.permissions),
    [permissionMenus, user?.permissions],
  );

  const menuAccess = useMemo(
    () =>
      resolveMenuAccessByPath({
        pathname: location.pathname,
        menuKeyByUrlPath,
        permissions: user?.permissions,
        role: user?.role,
      }),
    [
      location.pathname,
      menuKeyByUrlPath,
      user?.permissions,
      user?.role,
    ],
  );

  const sidebarItems = useMemo(() => {
    // 설정은 헤더 프로필에서만 진입 — 설정 모드 사이드바는 SETTINGS 권한 순서
    if (inSettingsMode) {
      return buildSettingsSidebarFromMenuTree(
        permissionMenus,
        user?.role,
        user?.roles,
      );
    }
    const fromPermissions = buildSidebarFromMenuTree(permissionMenus, {
      permissions: user?.permissions,
      role: user?.role,
    });
    if (fromPermissions) return fromPermissions;
    // urlPath가 없거나 카탈로그와 안 맞으면 하드코딩 + 권한 필터
    return filterSidebarByPermission(
      adminSidebarMenuItems,
      user?.permissions,
      user?.role,
    );
  }, [
    inSettingsMode,
    permissionMenus,
    user?.permissions,
    user?.role,
    user?.roles,
  ]);

  useEffect(() => {
    if (!isInitialized) {
      initialize();
    }
  }, [isInitialized, initialize]);

  useEffect(() => {
    if (isInitialized && !isAuthenticated) {
      navigate({ to: '/' });
    }
  }, [isInitialized, isAuthenticated, navigate]);

  // 임시 비밀번호 상태면 관리 화면 진입을 막고 비밀번호 변경으로 유도
  useEffect(() => {
    if (!isInitialized || !isAuthenticated) return;
    if (!user?.mustChangePassword) return;
    navigate({ to: ROUTE_PATHS.FORCE_CHANGE_PASSWORD } as never);
  }, [
    isInitialized,
    isAuthenticated,
    user?.mustChangePassword,
    navigate,
  ]);

  useEffect(() => {
    if (!isInitialized || !isAuthenticated) return;
    if (hasAccess) return;
    logout();
    navigate({ to: '/' });
  }, [isInitialized, isAuthenticated, hasAccess, logout, navigate]);

  // 일반 계정이 마스터·관리 전용 설정 URL로 들어오면 내 프로필로 되돌림
  useEffect(() => {
    if (!isInitialized || !isAuthenticated || !hasAccess) return;
    const needsFullAccess = SETTINGS_FULL_ACCESS_PATHS.some(
      (path) =>
        location.pathname === path ||
        location.pathname.startsWith(`${path}/`),
    );
    if (!needsFullAccess) return;
    if (canOpenAccountManagement(user?.role)) return;
    navigate({ to: ROUTE_PATHS.MY_ACCOUNT as never });
  }, [
    isInitialized,
    isAuthenticated,
    hasAccess,
    location.pathname,
    user?.role,
    navigate,
  ]);

  // 메뉴 설정 관리(`/admin/settings/menus`) — ROLE_MASTER만. 서버도 403을 주지만 UX로 선차단.
  useEffect(() => {
    if (!isInitialized || !isAuthenticated || !hasAccess) return;
    const onMenus =
      location.pathname === ROUTE_PATHS.SETTINGS_MENUS ||
      location.pathname.startsWith(`${ROUTE_PATHS.SETTINGS_MENUS}/`);
    if (!onMenus) return;
    const master =
      isMaster(user?.role) ||
      (user?.roles ?? []).some((role) => isMaster(role));
    if (master) return;
    navigate({ to: ROUTE_PATHS.MY_ACCOUNT as never });
  }, [
    isInitialized,
    isAuthenticated,
    hasAccess,
    location.pathname,
    user?.role,
    user?.roles,
    navigate,
  ]);

  // 메뉴 urlPath에 매칭된 경로인데 READ 미만이면 대시보드로 되돌림
  useEffect(() => {
    if (!isInitialized || !isAuthenticated || !hasAccess) return;
    if (!menuAccess.matched || menuAccess.canRead) return;
    toastApi.warning('해당 메뉴에 대한 조회 권한이 없습니다.');
    navigate({ to: ROUTE_PATHS.DASHBOARD as never });
  }, [
    isInitialized,
    isAuthenticated,
    hasAccess,
    menuAccess.matched,
    menuAccess.canRead,
    navigate,
  ]);

  if (
    !isInitialized ||
    !isAuthenticated ||
    !hasAccess ||
    user?.mustChangePassword
  ) {
    return <LoadingSpinner fullscreen tip="인증 확인 중..." />;
  }

  // READ 없는 매칭 경로 — 리다이렉트 전 잠깐 스피너
  if (menuAccess.matched && !menuAccess.canRead) {
    return <LoadingSpinner fullscreen tip="권한 확인 중..." />;
  }

  // 새 창으로 여는 화면(이전 처방 불러오기 등)은 인증·권한만 같이 쓰고 사이드바·헤더 없이 그린다
  if (isPopupWindowRoute(location.pathname)) {
    return (
      <div className="h-screen overflow-hidden bg-background">
        <MenuAccessContext.Provider value={menuAccess}>
          <Outlet />
        </MenuAccessContext.Provider>
      </div>
    );
  }

  const breadcrumbs = buildMenuBreadcrumbs(
    inSettingsMode
      ? { path: ROUTE_PATHS.MY_ACCOUNT, label: '설정', icon: Home }
      : { path: ROUTE_PATHS.DASHBOARD, label: '홈', icon: Home },
    sidebarItems,
    location.pathname,
  );

  const exitSettings = () => {
    navigate({ to: ROUTE_PATHS.DASHBOARD as never });
  };

  const settingsFooter = inSettingsMode ? (
    <button
      type="button"
      onClick={exitSettings}
      title="설정 종료"
      className={cn(
        'flex h-9 w-full items-center gap-2.5 rounded-12 px-2.5 text-sm font-medium text-slate-400 transition-colors hover:bg-white/5 hover:text-slate-200',
        collapsed && 'justify-center px-2',
      )}
    >
      <DoorOpen className="h-4 w-4 shrink-0" />
      {!collapsed && <span className="truncate">설정 종료</span>}
    </button>
  ) : undefined;

  return (
    <div className="admin-layout flex h-dvh overflow-hidden">
      <SidebarMenu
        items={sidebarItems}
        subtitle={inSettingsMode ? 'Settings' : 'Admin'}
        footer={settingsFooter}
      />

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="ruda-header flex h-16 shrink-0 items-center justify-between gap-2 px-3 sm:px-4 xl:h-20 xl:px-6">
          <div className="flex min-w-0 items-center gap-2 sm:gap-3">
            <Button
              variant="ghost"
              size="icon"
              aria-label={isMobileLayout ? '메뉴 열기' : '사이드바 접기/펼치기'}
              onClick={() =>
                isMobileLayout
                  ? setMobileSidebarOpen(true)
                  : setSidebarCollapsed(!sidebarCollapsed)
              }
              className="shrink-0 text-muted-foreground"
            >
              <Menu className="h-4 w-4" />
            </Button>

            <LayoutBreadcrumb segments={breadcrumbs} />
          </div>

          <UserMenu />
        </header>

        <div className="ruda-admin-container flex-1 overflow-y-auto overflow-x-hidden">
          <main className="ruda-content">
            <MenuAccessContext.Provider value={menuAccess}>
              <Outlet />
            </MenuAccessContext.Provider>
          </main>
        </div>
      </div>

      {/* 레거시 MainLayout #cover-spin — OCR·확정 등 장시간 작업 차단 */}
      {globalLoadingCount > 0 ? (
        <LoadingSpinner
          fullscreen
          tip={globalLoadingTip ?? '처리 중...'}
          className="z-[100] bg-background/70"
        />
      ) : null}
    </div>
  );
};
